import { type OrdersSearchParameters, type OrdersV2APIParameters } from '../types/ordersV2.type';

type MappingParameterFunction = (
  clientParameters: OrdersSearchParameters,
) => Partial<OrdersV2APIParameters>;

/**
 * Transforms ?listKey[]=1&listKey[]=2&listKey[]=3 into { listKey: ['1', '2', '3'] }
 */
const getParameterAsList = (parameters: OrdersSearchParameters, listKey: string) =>
  Object.entries(parameters)
    .filter(([key, value]) => key.startsWith(`${listKey}[`) && value !== undefined)
    .map(([, value]) => value as string);

/**
 * Removes empty parameters from the Orders V2 API parameters object.
 * This includes parameters that are undefined, empty strings, or empty arrays/objects.
 */
const clearEmptyParameters = (parameters: OrdersV2APIParameters): Partial<OrdersV2APIParameters> =>
  Object.fromEntries(
    Object.entries(parameters).filter(([, value]) => {
      if (value === undefined) return false;
      if (value === '') return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return Object.keys(value).length > 0;

      return true;
    }),
  ) as Partial<OrdersV2APIParameters>;

const handlePagination: MappingParameterFunction = ({ page, results }) => ({
  page: page ? parseInt(page, 10) : undefined,
  results: results ? parseInt(results, 10) : undefined,
});

const handleSortingAndOrdering: MappingParameterFunction = ({ order_by, sort_by }) => ({
  order_by,
  sort_by,
});

const handleSearchField: MappingParameterFunction = ({ q }) => ({
  q,
});

const handleOrderStatus: MappingParameterFunction = ({ order_status }) => {
  if (order_status === 'hidden') return { hidden: true };
  if (order_status === 'fulfilled') return { order_status: ['SHIPPED'] };
  if (order_status === 'cancelled') return { order_status: ['CANCELLED'] };
  if (order_status === 'pending')
    return { order_status: ['PAID', 'UNKNOWN', 'AWAITPAY', 'PARTIALLY_FULFILLED'] };
  return {};
};

const handleTags: MappingParameterFunction = ({ untagged, ...parameters }) => ({
  tag_id: getParameterAsList(parameters, 'tag'),
  untagged: untagged === undefined ? untagged : untagged === 'true',
});

const handleDestination: MappingParameterFunction = ({ is_international }) => {
  if (is_international === 'true') return { destination: 'international' };
  if (is_international === 'false') return { destination: 'domestic' };
  return {};
};

const handleDate: MappingParameterFunction = ({ end_date, start_date }) => ({
  end_date,
  start_date,
});

const handleStores: MappingParameterFunction = ({ shop_app, ...parameters }) => ({
  shop_app,
  source: getParameterAsList(parameters, 'source'),
});

const handleItems: MappingParameterFunction = ({ ...parameters }) => ({
  item: getParameterAsList(parameters, 'item'),
});

const handleWeight: MappingParameterFunction = ({ weight_gt, weight_lt, weight_unit }) => ({
  weight_gt: weight_gt ? parseFloat(weight_gt) : undefined,
  weight_lt: weight_lt ? parseFloat(weight_lt) : undefined,
  weight_unit,
});

const handleShippingMethod: MappingParameterFunction = ({ ...parameters }) => ({
  shipping_method: getParameterAsList(parameters, 'shipping_method'),
});

const handleAddressFrom: MappingParameterFunction = ({ ...parameters }) => ({
  address_from: getParameterAsList(parameters, 'address_from'),
});

const handleItemQuantity: MappingParameterFunction = ({
  total_item_quantity_max,
  total_item_quantity_min,
}) => ({
  total_item_quantity_max: total_item_quantity_max
    ? parseInt(total_item_quantity_max, 10)
    : undefined,
  total_item_quantity_min: total_item_quantity_min
    ? parseInt(total_item_quantity_min, 10)
    : undefined,
});

export const mapSearchParametersToAPIParameters = (
  searchParameters: OrdersSearchParameters | null,
): OrdersV2APIParameters | null => {
  if (!searchParameters) return null;

  return clearEmptyParameters({
    ...handlePagination(searchParameters),
    ...handleSortingAndOrdering(searchParameters),
    ...handleSearchField(searchParameters),
    ...handleOrderStatus(searchParameters),
    ...handleTags(searchParameters),
    ...handleDestination(searchParameters),
    ...handleDate(searchParameters),
    ...handleStores(searchParameters),
    ...handleItems(searchParameters),
    ...handleWeight(searchParameters),
    ...handleItemQuantity(searchParameters),
    ...handleShippingMethod(searchParameters),
    ...handleAddressFrom(searchParameters),
  });
};
